import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button, LoadingSpinner, ErrorMessage } from '../components/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { usePermitHistory } from '../hooks/api/usePermits';

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  return date.toLocaleDateString();
}

export default function PermitSearchHistoryPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { data, isLoading, error } = usePermitHistory(page, pageSize);

  const totalPages = data ? Math.ceil(data.total_count / pageSize) : 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/dashboard">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Search History</h1>
        <p className="text-gray-600 mt-1">View all your past permit searches</p>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {error && (
        <ErrorMessage
          title="Failed to load search history"
          message="There was an error loading your search history. Please try again."
        />
      )}

      {!isLoading && !error && data && data.searches.length === 0 && (
        <Card variant="default" padding="lg" className="text-center">
          <CardContent>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No searches yet</h2>
            <p className="text-gray-600 mb-4">
              Start by searching for permit requirements and fees for your next project.
            </p>
            <Link to="/permits/search">
              <Button variant="primary">Start Your First Search</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && data && data.searches.length > 0 && (
        <>
          <Card variant="elevated" padding="lg" className="mb-6">
            <CardHeader className="mb-4">
              <CardTitle as="h2">
                All Searches ({data.total_count})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.searches.map((search) => (
                  <Card key={search.id} variant="default" padding="md">
                    <CardContent>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {search.city}, {search.state}
                          </h3>
                          {search.municipality_name && (
                            <p className="text-sm text-gray-600 mb-2">
                              {search.municipality_name}
                            </p>
                          )}
                          <p className="text-sm text-gray-700 line-clamp-2 mb-2">
                            {search.job_description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                        <div>
                          {search.total_estimated_fee !== undefined && search.total_estimated_fee !== null && (
                            <p className="text-lg font-bold text-primary">
                              ${search.total_estimated_fee.toFixed(2)}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            {formatTimeAgo(search.created_at)}
                          </p>
                        </div>
                        <Link to={`/permits/${search.id}`}>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
